import React, { useEffect } from 'react';

import GridIcon from '../../images/GridIcon';
import { lazyLoading } from '../../utils/lazyLoading';
import { PrimaryButton } from '../Buttons/PrimaryButton';
import DynamicHeight from '../DynamicHeight';

import style from './Hero.scss';

import cn from 'classnames';
import { graphql, Link, StaticQuery } from 'gatsby';

const Hero = (props: any) => {
  const { data } = props;

  const [isHeroExpanded, setIsHeroExpanded] = React.useState(false);

  const toggleExpandHero = () => {
    setIsHeroExpanded(!isHeroExpanded);
  };

  useEffect(() => {
    lazyLoading();
  }, []);

  return (
    <div className={cn('container', style.container)}>
      <div className={cn('row justify-content-center', style.hero)}>
        <div className="col-12 col-lg-9">
          <small>DESIGN, BUILD AND ENJOY YOUR LIFE</small>
          <h1>A personal finance blog for navigating life on your terms</h1>
        </div>
        <div className={cn('col-12', style.heroContent)}>
          <div className={style.topicGrid}>
            <Link to="/tag/side-hustle/" className={style.topic}>
              <img
                src={data.sideHustle.childImageSharp.fixed.src}
                className={style.image}
                alt="Side Hustles"
                width="170"
                height="170"
              />
              <p>Side Hustles</p>
            </Link>
            <Link to="/tag/saving-money/" className={style.topic}>
              <img
                src={data.savingMoney.childImageSharp.fixed.src}
                className={style.image}
                alt="Saving Money"
                width="170"
                height="170"
              />
              <p>Saving Money</p>
            </Link>
            <Link to="/tag/tools/" className={style.topic}>
              <img
                src={data.financialTools.childImageSharp.fixed.src}
                className={style.image}
                alt="Financial Tools"
                width="170"
                height="170"
              />
              <p>Financial Tools</p>
            </Link>
            <Link to="/tag/investing/" className={style.topic}>
              <img
                src={data.investment.childImageSharp.fixed.src}
                className={style.image}
                alt="Investing"
                width="170"
                height="170"
              />
              <p>Investing</p>
            </Link>
            <DynamicHeight isExpanded={isHeroExpanded} className={style.topicGrid}>
              <Link to="/tag/passive-income/" className={style.topic}>
                <img
                  data-src={data.passiveIncome.childImageSharp.fixed.src}
                  className={cn('lazy', style.image)}
                  alt="Passive Income"
                  width="170"
                  height="170"
                />
                <p>Passive Income</p>
              </Link>
              <Link to="/tag/getting-out-of-debt/" className={style.topic}>
                <img
                  data-src={data.talkingDebt.childImageSharp.fixed.src}
                  className={cn('lazy', style.image)}
                  alt="Getting out of debt"
                  width="170"
                  height="170"
                />
                <p>Tackling Debt</p>
              </Link>
              <Link to="/tag/financial-literacy/" className={style.topic}>
                <img
                  data-src={data.financialLiteracy.childImageSharp.fixed.src}
                  className={cn('lazy', style.image)}
                  alt="Financial Literacy"
                  width="170"
                  height="170"
                />
                <p>Financial Literacy</p>
              </Link>
              <Link to="/tag/digital-nomad-life/" className={style.topic}>
                <img
                  data-src={data.domesticNomad.childImageSharp.fixed.src}
                  className={cn('lazy', style.image)}
                  alt="Digital Nomad components"
                />
                <p>Digital Nomad Life</p>
              </Link>
              <Link to="/tag/domestic-engineer/" className={style.topic}>
                <img
                  data-src={data.domesticEngineer.childImageSharp.fixed.src}
                  className={cn('lazy', style.image)}
                  alt="Domestic Engineer"
                  width="170"
                  height="170"
                />
                <p>Domestic Engineering</p>
              </Link>
              <Link to="/tag/credit/" className={style.topic}>
                <img
                  data-src={data.creditHabits.childImageSharp.fixed.src}
                  className={cn('lazy', style.image)}
                  alt="Credit"
                  width="170"
                  height="170"
                />
                <p>Credit Habits</p>
              </Link>
              <Link to="/tag/budget/" className={style.topic}>
                <img
                  data-src={data.budgetHacks.childImageSharp.fixed.src}
                  className={cn('lazy', style.image)}
                  alt="Budget"
                  width="170"
                  height="170"
                />
                <p>Budget Hacks</p>
              </Link>
              <Link to="/tag/retire-early/" className={style.topic}>
                <img
                  data-src={data.retireEarly.childImageSharp.fixed.src}
                  className={cn('lazy', style.image)}
                  alt="Retire Early"
                  width="170"
                  height="170"
                />
                <p>Retire Early</p>
              </Link>
            </DynamicHeight>
            <PrimaryButton onClick={toggleExpandHero}>
              <GridIcon />
              {`${isHeroExpanded ? 'Hide Topics' : 'Show All Topics'}`}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export const heroImage = graphql`
  fragment heroImage on File {
    childImageSharp {
      fixed(width: 170, height: 170) {
        ...GatsbyImageSharpFixed
      }
    }
  }
`;

const HeroSectionQuery = () => {
  const renderHeroSection = (data: any) => <Hero data={data} />;
  return (
    <StaticQuery
      query={graphql`
        query HeroSection {
          answerYourself: file(relativePath: { eq: "Home/answerYourself.png" }) {
            ...heroImage
          }
          budgetHacks: file(relativePath: { eq: "Home/budgetHacks.png" }) {
            ...heroImage
          }
          calculatePayment: file(relativePath: { eq: "Home/calculatePayment.png" }) {
            ...heroImage
          }
          creditHabits: file(relativePath: { eq: "Home/creditHabits.png" }) {
            ...heroImage
          }
          domesticEngineer: file(relativePath: { eq: "Home/domesticEngineer.png" }) {
            ...heroImage
          }
          domesticNomad: file(relativePath: { eq: "Home/domesticNomad.png" }) {
            ...heroImage
          }
          featuredLg: file(relativePath: { eq: "Home/featuredLg.svg" }) {
            ...heroImage
          }
          featuredSm: file(relativePath: { eq: "Home/featuredSm.svg" }) {
            ...heroImage
          }
          financialLiteracy: file(relativePath: { eq: "Home/financialLiteracy.png" }) {
            ...heroImage
          }
          financialTools: file(relativePath: { eq: "Home/financialTools.png" }) {
            ...heroImage
          }
          growMoney: file(relativePath: { eq: "Home/growMoney.png" }) {
            ...heroImage
          }
          investment: file(relativePath: { eq: "Home/investment.png" }) {
            ...heroImage
          }
          passiveIncome: file(relativePath: { eq: "Home/passiveIncome.png" }) {
            ...heroImage
          }
          retireEarly: file(relativePath: { eq: "Home/retireEarly.png" }) {
            ...heroImage
          }
          savingMoney: file(relativePath: { eq: "Home/savingMoney.png" }) {
            ...heroImage
          }
          sideHustle: file(relativePath: { eq: "Home/sideHustle.png" }) {
            ...heroImage
          }
          talkingDebt: file(relativePath: { eq: "Home/talkingDebt.png" }) {
            ...heroImage
          }
          tsr: file(relativePath: { eq: "Home/tsr.png" }) {
            ...heroImage
          }
        }
      `}
      render={renderHeroSection}
    />
  );
};

export default HeroSectionQuery;
